import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleLogin from "./components/GoogleLogin";
import Layout from "./components/Layout";
import ProtectedRoute from "./ProtectedRoute";
import { getSession } from "./utils/session";
import { logoutUser } from "./utils/auth";

const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getSession());

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
  }, []);

  // ----------------------------------------
  // 🔥 AUTO LOGOUT ON USER INACTIVITY (15 minutes)
  // ----------------------------------------
  useEffect(() => {
    if (!isAuthenticated) return;

    let timeout;
    const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes

    // Reset inactivity timer
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        alert("You have been logged out due to 15 minutes of inactivity.");
        logoutUser();
      }, INACTIVITY_LIMIT);
    };

    // Activity events
    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer(); // Start timer on login

    return () => {
      clearTimeout(timeout);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated, handleLogout]);

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <Router>
        <Routes>
          {/* Public Route */}
          {!isAuthenticated && (
            <Route path="/" element={<GoogleLogin onLoginSuccess={handleLoginSuccess} />} />
          )}

          {/* Protected Layout */}
          {isAuthenticated && (
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            />
          )}

          {/* Default fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </GoogleOAuthProvider>
  );
};

export default App;
